import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface GeoLocation {
  latitude: number;
  longitude: number;
}

@Injectable({
  providedIn: 'root' // Makes the service a singleton available throughout the app
})
export class LocationService implements OnDestroy {

  // BehaviorSubject to store and emit the current location
  // It emits null initially, and then the latest location
  private _currentLocation = new BehaviorSubject<GeoLocation | null>(null);
  public readonly currentLocation$ = this._currentLocation.asObservable(); // Public observable

  private _currentLocationError = new BehaviorSubject<number | null>(null);
  public readonly currentLocationError$ = this._currentLocationError.asObservable(); // Public observable
  
  private _locationInProgress = new BehaviorSubject<boolean>(false);
  public readonly locationInProgress$ = this._locationInProgress.asObservable(); // Public observable for progress

  private watchId: number | null = null; // ID for geolocation watch

  constructor() {
    console.log('LocationService: Initializing and starting location watch.');
    this.startWatchingLocation();
  }

  private startWatchingLocation(): void {
    if (navigator.geolocation) {
      this._locationInProgress.next(true);
      this.watchId = navigator.geolocation.watchPosition(
        (position) => {
          const newLocation: GeoLocation = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          };
          this._currentLocation.next(newLocation);
          this._currentLocationError.next(null);
          this._locationInProgress.next(false);
        },
        (error) => {
          console.error('LocationService: Error watching location', error);
          this._currentLocation.next(null); // Emit null if location becomes unavailable
          this._currentLocationError.next(error.code);
          this._locationInProgress.next(false);
        },
        // Options for watchPosition:
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 } // High accuracy, 15s timeout, no cached positions
      );
    } else {
      console.warn('LocationService: Geolocation is not supported by this browser.');
      this._currentLocation.next(null); // Emit null if geolocation is not supported
      this._currentLocationError.next(-1);
      this._locationInProgress.next(false);
    }
  }

  // Method to manually stop watching location (e.g., if user logs out)
  stopWatchingLocation(): void {
    if (this.watchId !== null) {
      navigator.geolocation.clearWatch(this.watchId);
      this.watchId = null;
      console.log('LocationService: Stopped watching location.');
    }
    this._currentLocation.next(null); // Clear the current location
  }

  // ngOnDestroy is called when the service provider is destroyed.
  // For services providedIn: 'root', this often means app shutdown.
  ngOnDestroy(): void {
    console.log('LocationService: ngOnDestroy called. Stopping location watch.');
    this.stopWatchingLocation();
  }
}